import { store } from '../core/store.js';
import { PRESETS_VALIDOS, DENSIDADES_VALIDAS, BTN_STYLES_VALIDOS } from './theme-tokens.js';
import { inyectarDivider, separadoresDe, recorteDe, SIN_SEPARADORES } from './dividers.js';
import { inyectarFondo } from '../animations/fondos/inyectar.js';
import { anadirAtributos, insertarTrasTag, RE_SECCION } from '../utils/tag.js';
import { dimensionesDe } from '../utils/dimension.js';
import { blockRegistry } from '../blocks/registry.js';
import { getFontVariables, loadFontsForPreset, cargarFuentesDePreset, cargarHojaDeFuente } from '../utils/typography.js';
import { inyectarTituloSeccion, resaltesDe, serifDeCursiva, urlDeCursiva } from '../utils/titulo-estilo.js';
import { inyectarTextoSeccion, fuenteSegura } from '../utils/texto-seccion.js';
import { sePublica, bloquesPublicados } from '../utils/publicable.js';
import { pointsToSVGPath } from '../editor/svg-editor-utils.js';
import { SHAPE_PRESETS, tieneFormaValida } from '../editor/shape-presets.js';
import { getVariantIds, propsConVarianteSegura, getBlockMeta } from '../blocks/block-meta.js';
// Barra del bloque (rediseño Nocturne). Solo la usa `construirBloqueHtml`, que el bundle PÚBLICO no
// alcanza: esbuild deja los iconos fuera de public-init.js (lo vigila tests/test_barra_bloque.py).
import { ph } from '../utils/icons-ph.js';
import { especDeClase, htmlBotonBarra, nombreDeBloque, TIP_PROTEGIDA } from '../utils/barra-bloque.js';
import { admiteRecorte, modoImagen } from '../utils/imagen-recorte.js';
import { aplicarDestinoDeSitio } from '../utils/cta-destino.js';
import { aplicarDestinosDeBotones, contextoDeBotones } from '../utils/destino-boton.js';
import { inyectarAncla, estaEnLaCabecera } from '../utils/ancla-seccion.js';
import { anclaPropia, idDeSeccion } from '../utils/secciones.js';
import { getRenderLang, t } from '../utils/i18n.js';
import { secondaryOf, cssTokenSafe } from '../utils/contrast.js';
import { aplicarBotonWeb } from '../utils/boton-estilo.js';
import { escapeHtml } from '../utils/dom.js';
import { reservaDeCabecera, reservarHueco } from './hueco-cabecera.js';

const postRenderHooks = [];

/**
 * Registra una función que se ejecutará tras cada renderPage().
 * @param {Function} fn - Callback sin argumentos.
 * @param {Object} [opts] - { editorOnly: bool, previewOnly: bool }
 */
export function registerPostRender(fn, opts = {}) {
    postRenderHooks.push({ fn, ...opts });
}


// ═══════════════════════════════════════════════════════════════════════════
//  TOKENS DE TEMA — SSOT compartida por render.js (editor) y compileSiteHtml
//  (publicado). Toda esta sección es de FUNCIONES PURAS salvo applyTokensOnly;
//  viewer.js las importa de aquí para no duplicar la política (gotcha #12: lo
//  que se inyecta en el <section> tiene que ser idéntico en ambas superficies).
//
//  INVARIANTE DEL GRUPO: todos estos tokens/atributos son OPT-IN. Mientras
//  `site_tokens` no los traiga, ni se emiten ni cambian nada: un sitio ya
//  generado se sigue viendo exactamente igual.
// ═══════════════════════════════════════════════════════════════════════════

// Los tres enums de tema (presets / densidades / estilos de botón) VIVEN en
// `core/theme-tokens.js`, un módulo PURO: render.js no es importable fuera del
// navegador (store.js + DOM), así que `build.mjs` no podía leerlos de aquí para
// materializarlos a `core_services/theme_tokens.json` — que es lo que permite que
// el schema del LLM y el saneo de views.py DERIVEN estas listas en vez de copiarlas.
// Se re-exportan para no cambiarle la API a nadie: quien los quiera puede seguir
// importándolos de render.js (o, mejor, del módulo puro).
export { PRESETS_VALIDOS, DENSIDADES_VALIDAS, BTN_STYLES_VALIDOS } from './theme-tokens.js';

/**
 * Ancho de contenido que PROPONE cada densidad. Es la pata de "ancho por defecto"
 * del control único: se resuelve en el render (rellenando `data-content-width`
 * cuando el bloque no trae el suyo) en vez de duplicar en CSS el aplicador de
 * `--cw`, que es una regla enorme y frágil. El ajuste por bloque SIEMPRE manda.
 */
export const DENSITY_CONTENT_WIDTH = { compact: 'wide', cozy: '', editorial: 'narrow' };

/**
 * BLOQUES DE CABECERA — SSOT del "¿quién hace de hero en esta página?".
 *
 * El hero NO es obligatorio por schema: la obligación ([0]=header → [1]=hero → …) es prosa
 * del prompt. Pero una docena de puntos del código asumía que existe y TODOS degradaban en
 * silencio si no (fondo de #resultado, fuente del sitio, `<h1>`, ritmo de página, SEO…).
 * Desde que el `carrusel` puede ocupar ese puesto, la respuesta vive AQUÍ y no en literales
 * `'hero'` repartidos. ESPEJO en Python: `core_services/block_registry.py::TIPOS_HERO`.
 */
export const TIPOS_HERO = ['hero', 'carrusel'];

/**
 * Clases de <section> que YA reservan en su propio CSS el hueco del header fijo
 * (`.carrusel-section` sobre el pie de foto, proporcional a cada altura; `.imagen-section`
 * con el suyo, ver más abajo). `applyFirstSectionHeaderOffset` las salta: añadirles
 * padding-top desde JS duplicaría el hueco y, en un bloque full-bleed, metería una banda de
 * color sobre la foto.
 *
 * `.hero-section` YA NO está (25-09-2026): «reservaba» con un `padding-top: 180px` que nunca
 * se aplicaba —lo pisa `#resultado section { padding-block }` (1-0-1) y, con `data-spacing`,
 * sus `!important`—, así que en «Ver sitio» y en el publicado la cabecera fija se comía su alto
 * entero (titular a 44 px de ella, frente a 183 en el editor). Ahora recibe lo mismo que
 * cualquier primera sección: su padding propio + lo que tapa la cabecera. Su foto es el FONDO
 * de la sección, así que el hueco no abre ninguna banda: la foto sigue llegando hasta arriba.
 *
 * `.imagen-section` NO estaba y era un bug de las DOS mitades: el bloque emite su <section>
 * con `padding:0` (site.css) porque su v1 va a sangre, así que el `padding-top` **inline
 * !important** que escribía el offset (entonces `headerH+24`) abría una banda de `--bg` POR ENCIMA de la
 * foto — en preview y en el publicado (public-init.js llama a la misma función). La otra
 * mitad (que el CSS reserve el hueco cuando `imagen` abre la página) vive en site.css, en
 * el bloque `main > .imagen-section:first-child`.
 *
 * DECISIÓN (documentada también en site.css): en la v1 la FOTO pasa POR DEBAJO del header
 * fijo, que es lo natural en un full-bleed y lo que ya hacen la foto del hero y la del
 * carrusel; lo único que se aparta del header es el BOTÓN que va sobre ella. La v2
 * (contenida) sí reserva el hueco con padding, porque ahí la foto va enmarcada y quedar
 * medio tapada por el header no es un efecto, es un recorte.
 */
export const CLASES_RESERVAN_HEADER = ['carrusel-section', 'imagen-section'];

/**
 * Ancho de contenido que aporta la densidad para ESTE bloque ('' = no aportar).
 * Los bloques de CABECERA (TIPOS_HERO) quedan fuera por decisión de producto (ya
 * documentada en el aplicador de `--cw` en site.css: "el hero usa siempre su diseño
 * propio, sin panel de ajustes"), y además el nivel 'wide' reescribe el padding lateral,
 * que en ellos es su composición (y el carrusel es full-bleed). El chrome
 * (header/footer/cta-bar) no es una sección de contenido.
 */
// `ai-custom` entra por lo MISMO que `imagen`: su contenido es un `<iframe>` —hijo directo del
// <section>— y el aplicador de --cw lo capaba a 720 px con la densidad «editorial» sin que el
// cliente pudiera cambiarlo (no tiene «Ajustes de sección»). Dentro del iframe, `100vw` es el
// ancho del propio iframe: el bloque no podía salir de esa columna. La exclusión gemela para lo
// YA publicado (que lleva el `data-content-width` horneado) vive en site.css (`:not(.ai-custom-frame)`).
export const TIPOS_SIN_ANCHO = ['imagen', 'ai-custom'];

export function anchoPorDensidad(density, tipo, props) {
    // `imagen` fuera por lo MISMO que TIPOS_HERO, y hay que decirlo en los TRES sitios o no
    // sirve de nada: el editor ya no le ofrece "Ajustes de sección" (tools.js) y el backend lo
    // saca de `_LAYOUT_EXCLUDE`, pero la DENSIDAD del sitio inyecta `data-content-width` por
    // este otro camino. Con él, el aplicador de --cw de site.css capa `.imagen-marco` —hijo
    // directo del <section>— y la variante "a sangre" queda encajonada en una columna.
    if (!density || TIPOS_HERO.indexOf(tipo) >= 0 || _TIPOS_CHROME.indexOf(tipo) >= 0
        || TIPOS_SIN_ANCHO.indexOf(tipo) >= 0) return '';
    const ancho = DENSITY_CONTENT_WIDTH[density] || '';
    // `narrow` por DENSIDAD no apila un bloque REDIMENSIONABLE (content v1/v3 con imagen): el
    // `narrow` de content va a UNA columna con reglas `!important`, esconde las asas y quita el
    // panel — caso real: densidad "editorial" dejaba todos los `content` del sitio con la foto
    // encima del texto y "no me deja redimensionar nada". La densidad sugiere un ancho; apilar
    // la fila es otra cosa y solo la decide el cliente con el knob de Ajustes de sección
    // (`props.content_width`, que manda antes de llegar aquí).
    if (ancho === 'narrow' && props && dimensionesDe(tipo, props.variant || 'v1')
        && (tipo !== 'content' || props.use_image)) return '';
    return ancho;
}

/**
 * CADENA DE DOS NIVELES (ver la tabla de presets en css/site.css): los tokens que
 * el cliente YA gradúa con un slider viajan ADEMÁS por el canal `--u-*`. Motivo:
 * el preset declara el token con su nombre normal EN LA SECCIÓN, y una declaración
 * en la sección gana a la heredada de #resultado — sin este alias, activar un preset
 * borraría el valor del slider en todos los sitios ya generados.
 * NO se aliasa `--card-bg`/`--card-border`: los escribe la IA, no el cliente (el
 * `!important` que había existía justo para pisarlos).
 */
export const U_TOKEN_ALIAS = {
    '--card-radius': '--u-card-radius',
    '--btn-radius': '--u-btn-radius',
    // `--type-scale` lo gradúa el slider «Titulares y encabezados» del Estudio de Tema, pero
    // `[data-density]` lo REDECLARA en cada <section> (compacto 0.92 / cómodo 1 / editorial
    // 1.08) → sin este canal, elegir una densidad dejaba el slider mudo. Misma regla que el
    // resto: la densidad PROPONE (fallback) y el cliente DISPONE (--u-type-scale).
    '--type-scale': '--u-type-scale',
    // «Párrafos y subtítulos» (F3, 16-10-2026). La densidad NO lo declara, así que hoy el alias no
    // cambia nada; existe para que un ajuste POR SECCIÓN (F4) pueda redeclarar
    // `--text-scale: calc(var(--u-text-scale, 1) * k)` sin referirse a sí mismo.
    '--text-scale': '--u-text-scale',
};

/**
 * Tokens que hay que RE-DECLARAR dentro de cada <section>: su valor se tiñe con
 * `color-mix(… var(--text) …)` y `--text` solo existe INLINE por sección (en
 * #resultado quedarían *guaranteed-invalid* y toda regla caería a su fallback).
 */
export const SECTION_TOKENS = ['--shadow-card', '--shadow-hover'];

/**
 * Allowlist de caracteres de un valor CSS legítimo. `site_tokens` llega de
 * datos_json_builder, que se guarda SIN validar valores, y en varios sitios se
 * interpola como MARKUP (style inline de la sección, bloque <style> del head): un
 * `"` rompería el atributo y un `</style>` el elemento.
 *
 * SE DEFINE EN `utils/contrast.js` y aquí solo se RE-EXPORTA: `safe()` la necesita para el
 * mismo filtro sobre los props de color de cada bloque, y contrast.js no puede importar de
 * este módulo (render.js importa `secondaryOf` de allí → ciclo, y arrastra store.js/DOM a
 * todos los bloques). `viewer.js` la sigue importando DE AQUÍ: una sola copia, mismo sitio.
 */
export { cssTokenSafe };

/** Devuelve `valor` si está en la lista permitida; si no, ''. */
function _enum(valor, permitidos) {
    const v = typeof valor === 'string' ? valor.trim() : '';
    return permitidos.indexOf(v) >= 0 ? v : '';
}

/** Lee del site_tokens los 3 atributos de tema ya validados. */
export function temaDeTokens(tokens) {
    const t = (tokens && typeof tokens === 'object') ? tokens : {};
    return {
        preset: _enum(t.style_preset, PRESETS_VALIDOS),
        // Preset de CONTRAPUNTO: '' (lo normal) → todas las secciones con el mismo.
        contrapunto: _enum(t.preset_contrapunto, PRESETS_VALIDOS),
        density: _enum(t.density, DENSIDADES_VALIDAS),
        btnStyle: _enum(t.btn_style, BTN_STYLES_VALIDOS),
    };
}

// Tipos que son CHROME, no "sección de contenido": no pueden ser el contrapunto
// ni cuentan como "última sección" (el pie y la barra fija no son una banda).
const _TIPOS_CHROME = ['header', 'footer', 'cta-bar'];

/**
 * ¿Esta posición lleva el preset de CONTRAPUNTO?
 *
 * `data-preset` se inyecta hoy con el MISMO valor en todas las secciones, así que
 * el sistema es perfectamente uniforme… y por eso se lee como plantilla. Romper la
 * uniformidad en 1-2 puntos ELEGIDOS es lo que hace que una web parezca diseñada.
 * Los dos puntos son DETERMINISTAS (dos compilaciones dan el mismo HTML):
 *   1. el bloque de PRECIOS — el momento de decisión de la página;
 *   2. la BANDA PRE-FOOTER — la última sección de contenido.
 * No requiere CSS nuevo: el atributo ya viaja por sección en editor y publicado.
 *
 * @param {string[]} tipos - tipos de bloque EN ORDEN (da igual si incluye chrome:
 *                           el chrome nunca es candidato, así que el resultado es
 *                           el mismo partiendo de los bloques o de las <section>).
 */
export function esSeccionContrapunto(tipos, i) {
    if (!Array.isArray(tipos) || i < 0 || i >= tipos.length) return false;
    const tipo = tipos[i];
    if (_TIPOS_CHROME.indexOf(tipo) >= 0 || TIPOS_HERO.indexOf(tipo) >= 0) return false;
    if (tipo === 'pricing') return true;
    let ultimo = -1;
    for (let k = 0; k < tipos.length; k++) {
        if (_TIPOS_CHROME.indexOf(tipos[k]) < 0) ultimo = k;
    }
    // Con una sola sección de contenido no hay "contrapunto" posible: sería TODA
    // la página. Se exige que haya al menos otra sección por delante.
    return i === ultimo && ultimo >= 2;
}

/** Preset que corresponde a la posición `i` (base o contrapunto). */
export function presetDeSeccion(tipos, i, base, contrapunto) {
    if (contrapunto && contrapunto !== base && esSeccionContrapunto(tipos, i)) return contrapunto;
    return base;
}

// Props que hacen de TITULAR del bloque (para medir su longitud).
const _PROPS_TITULO = ['title', 'banner_text'];

/**
 * Nº de caracteres del titular del bloque (0 si no tiene). Se emite como
 * `--title-len` y lo consume `--title-adj` en css/site.css: el mismo clamp()
 * servía para tres palabras o para catorce, y al traducir al alemán el titular
 * crece un 20-35% sin que nada reaccione.
 */
export function longitudTitular(props) {
    if (!props) return 0;
    for (let i = 0; i < _PROPS_TITULO.length; i++) {
        const v = props[_PROPS_TITULO[i]];
        if (typeof v === 'string' && v.trim()) {
            return v.replace(/<[^>]*>/g, '').trim().length;
        }
    }
    return 0;
}

// ── Jerarquía de titulares a nivel de PÁGINA ────────────────────────────────
// hero y video emitían AMBOS <h1> (dos <h1> en la misma página) y una subpágina
// sin ninguno de los dos se quedaba SIN <h1>. La decisión no puede vivir en el
// bloque: es una propiedad de la PÁGINA. Aquí se reetiqueta el HTML ya renderizado,
// conservando intactos `data-field` y `contenteditable` (el extractor empareja por
// data-field/data-index, no por etiqueta) y añadiendo un marcador de clase para que
// el CSS devuelva la tipografía anterior → cambio SEMÁNTICO, no visual.

/** Tipos cuyo renderer emite un <h1> propio. */
// `carrusel` emite el <h1> SOLO en su primera diapositiva (blocks/carrusel.js); el resto
// nacen como <h2>, así que un carrusel de 9 fotos sigue aportando un único <h1>.
export const TIPOS_TITULAR = ['hero', 'video', 'carrusel'];

/** Añade una clase a la cadena de atributos de una etiqueta de apertura. */
function _conClase(attrs, clase) {
    if (/\bclass\s*=\s*"/.test(attrs)) {
        return attrs.replace(/\bclass\s*=\s*"([^"]*)"/, (_m, c) => `class="${(c + ' ' + clase).trim()}"`);
    }
    return ` class="${clase}"` + attrs;
}

/** Reetiqueta el primer <de> que case `filtro` a <a>, añadiendo `clase`. */
function _reetiquetar(html, de, a, clase, filtro) {
    const abre = new RegExp('<' + de + '\\b([^>]*)>', 'i');
    const m = html.match(abre);
    if (!m) return null;
    if (filtro && !filtro.test(m[1])) return null;
    const iniFin = m.index + m[0].length;
    const cierre = html.indexOf('</' + de + '>', iniFin);
    if (cierre < 0) return null;
    return html.slice(0, m.index)
        + '<' + a + _conClase(m[1], clase) + '>'
        + html.slice(iniFin, cierre)
        + '</' + a + '>'
        + html.slice(cierre + de.length + 3);
}

/** Solo se promueve un titular de SECCIÓN (no el `brand` del pie, p.ej.). */
const _FILTRO_TITULO = /\bdata-field="(?:title|banner_text)"/i;

/**
 * Aplica la jerarquía de titulares al HTML de UN bloque.
 * @param {string}  html      HTML ya renderizado del bloque.
 * @param {string}  tipo      Tipo de bloque.
 * @param {boolean} yaHayH1   ¿un bloque anterior de la página ya se quedó el <h1>?
 * @returns {{html: string, tomaH1: boolean}}
 */
export function jerarquiaTitulares(html, tipo, yaHayH1) {
    const esTitular = TIPOS_TITULAR.indexOf(tipo) >= 0;
    if (esTitular) {
        if (!yaHayH1) return { html, tomaH1: /<h1\b/i.test(html) };
        // Ya hay <h1> en la página → este titular baja a <h2> conservando su look.
        const bajado = _reetiquetar(html, 'h1', 'h2', 'n3i-h2-as-h1', null);
        return { html: bajado || html, tomaH1: false };
    }
    if (yaHayH1 || _TIPOS_CHROME.indexOf(tipo) >= 0) return { html, tomaH1: false };
    // Página sin hero/video (subpáginas): el primer título de sección asume el <h1>.
    const subido = _reetiquetar(html, 'h2', 'h1', 'n3i-h1-as-h2', _FILTRO_TITULO);
    return subido ? { html: subido, tomaH1: true } : { html, tomaH1: false };
}

/**
 * Escribe SOLO los tokens de tema (custom properties + atributos data-* de tema),
 * sin regenerar una línea de HTML.
 *
 * POR QUÉ EXISTE: arrastrar un slider del Estudio de Tema llamaba a `renderPage`
 * una vez por FRAME → se regeneraba TODO el markup del sitio y se destruían y
 * reinstanciaban todos los fondos canvas. Esto es exactamente la parte de
 * `renderPage` que cambia al mover un slider. La consume el Estudio de Tema con
 * este nombre EXACTO; `renderPage` la llama también (DRY: una sola política).
 *
 * Idempotente: llamarla dos veces con los mismos tokens no cambia nada.
 *
 * @param {Object} tokens  site_tokens (`siteData.site_tokens`).
 * @param {Object} [opts]  { root: HTMLElement } — por defecto #resultado.
 */
export function applyTokensOnly(tokens, opts) {
    const zona = (opts && opts.root) || document.getElementById('resultado');
    if (!zona) return;
    const t = (tokens && typeof tokens === 'object') ? tokens : {};

    // 1) Custom properties globales + alias --u-* (cadena de dos niveles).
    //    setProperty es API DOM (no markup) → inmune a inyección; la allowlist se
    //    aplica solo donde el valor se interpola como texto (ver más abajo).
    Object.keys(t).forEach(key => {
        const value = t[key];
        if (key.indexOf('--') === 0 && value) {
            zona.style.setProperty(key, value);
            if (U_TOKEN_ALIAS[key]) zona.style.setProperty(U_TOKEN_ALIAS[key], value);
        }
    });

    // 2) Atributos de tema. En #resultado (ancestro de todo) y, sobre todo, en cada
    //    <section>: los presets derivan sus valores de var(--bg)/var(--text), que
    //    SOLO existen inline por sección (declarados en #resultado se resolverían una
    //    vez contra el fallback y heredarían ese mismo color a toda la web).
    const tema = temaDeTokens(t);
    _attr(zona, 'data-preset', tema.preset);
    _attr(zona, 'data-density', tema.density);
    _attr(zona, 'data-btn-style', tema.btnStyle);

    const secciones = Array.prototype.slice.call(zona.querySelectorAll('section'));
    // Tipo de cada sección: en el editor lo lleva el wrapper; en el publicado, la
    // propia <section> (compileSiteHtml inyecta data-type). Sirve para el contrapunto.
    const tipos = secciones.map(s => {
        const w = s.closest('[data-type]');
        return (w && w.getAttribute('data-type')) || '';
    });
    secciones.forEach((s, i) => {
        _attr(s, 'data-preset', presetDeSeccion(tipos, i, tema.preset, tema.contrapunto));
        _attr(s, 'data-density', tema.density);
        _attr(s, 'data-btn-style', tema.btnStyle);
        SECTION_TOKENS.forEach(k => {
            if (t[k] && cssTokenSafe(t[k])) s.style.setProperty(k, t[k]);
            else s.style.removeProperty(k);
        });
    });
    // El header/footer se hoistean fuera de #resultado, pero su CTA debe respetar el
    // estilo de botón GLOBAL: el visitante ve el mismo botón arriba y abajo.
    Array.prototype.slice.call(zona.querySelectorAll('header, footer'))
        .concat(Array.prototype.slice.call(document.querySelectorAll('#hoisted-header')))
        .forEach(el => _attr(el, 'data-btn-style', tema.btnStyle));

    // 3) El estilo de botón puesto A MANO para toda la web (`site_tokens.boton`, utils/boton-estilo.js):
    //    variables `--n3b-*` que nadie más declara, así que basta con ponerlas arriba y se heredan; la
    //    cabecera izada vive fuera de #resultado y las lleva aparte. Paridad: el `:root` de compileSiteHtml.
    aplicarBotonWeb(zona, t);
    Array.prototype.slice.call(document.querySelectorAll('#hoisted-header'))
        .forEach(el => aplicarBotonWeb(el, t));
}

/** Pone (o quita, si el valor es vacío) un atributo. */
function _attr(el, nombre, valor) {
    if (valor) el.setAttribute(nombre, valor);
    else el.removeAttribute(nombre);
}

/**
 * Repinta UNA sección en su sitio, sin tocar el resto de la página.
 *
 * POR QUÉ EXISTE
 * --------------
 * `renderPage` hace `#resultado.innerHTML = html`: arrasa y reconstruye las N secciones.
 * Para un cambio LOCAL (previsualizar una variante al pasar el ratón) eso provoca, a la
 * vez, cuatro efectos que se manifiestan como un solo bug:
 *   1. destruye y reinstancia TODOS los fondos canvas → el parpadeo;
 *   2. cambia la altura del documento → el navegador recorta el scroll y todo se
 *      desplaza bajo el cursor;
 *   3. dispara una ráfaga al MutationObserver de editor/tools.js (observa `document.body`
 *      con subtree:true) → reinyección de las herramientas de cada bloque;
 *   4. sustituye elementos bajo el puntero ENTRE `mousedown` y `mouseup`, así que el
 *      `click` no llega a componerse y la variante nunca se aplicaba.
 *
 * NO EJECUTA LOS HOOKS POST-RENDER a propósito: no aceptan raíz (`fn()` escanea el
 * documento), así que re-lanzarlos reinicializaría los fondos de TODA la página y
 * devolvería el parpadeo que esta función viene a quitar. Para una previsualización
 * efímera es lo correcto — la sección se ve con su layout real, sin runtime animado,
 * igual que las miniaturas del menú de variantes. Al CONFIRMAR, el caller hace un
 * `renderPage` completo y ahí sí se inicializa todo.
 *
 * @param {number} index   índice del bloque dentro de la página activa
 * @param {Array}  blocks  la página entera (el divider necesita mirar al siguiente)
 * @returns {boolean} true si repintó; **false si no pudo** (índice inexistente, tipo
 *   desconocido, header hoisteado en preview, error al construir) → el caller debe caer
 *   a `renderPage`. Nunca lanza.
 */
export function repintarBloque(index, blocks, opciones) {
    const zona = document.getElementById('resultado');
    if (!zona || !Array.isArray(blocks)) return false;
    const block = blocks[index];
    if (!block || !blockRegistry[block.type]) return false;

    const viejo = zona.querySelector(`.block-editor-wrapper[data-index="${index}"]`);
    if (!viejo) return false;   // p.ej. el header, que en preview se hoistea fuera de #resultado

    // El separador por RECORTE (core/dividers.js) es un PACTO entre DOS secciones: la de arriba
    // se recorta por abajo y la de abajo sube `H` px, se recorta por arriba y compensa con un
    // hueco. Este repintado quirúrgico sustituye UN wrapper, así que si el cambio de props altera
    // lo que le toca a una VECINA hay que caerse al repintado completo. Basta con que cambie el
    // `bgColor` —la condición del separador compara los dos fondos—, y una vecina con el recorte
    // viejo no es un detalle de color: o la solapa `H` px o le abre un boquete con el fondo de la
    // página. Devolver false es el camino de siempre (el caller hace `renderPage`).
    const _normVar = (v) => String(v || '').replace(/\s+/g, ' ').trim();
    const vecinaDesincronizada = [index - 1, index + 1].some((i) => {
        const w = zona.querySelector(`.block-editor-wrapper[data-index="${i}"]`);
        if (!w) return false;
        const rec = recorteDe(blocks, i);
        if ((w.getAttribute('data-recorte') || '') !== (rec ? rec.atributo : '')) return true;
        return !!rec && Object.keys(rec.vars)
            .some((k) => _normVar(w.style.getPropertyValue(k)) !== _normVar(rec.vars[k]));
    });
    if (vecinaDesincronizada) return false;

    // Los FONDOS ANIMADOS los crea un hook post-render (`initBackgroundAnimations`, que aquí no
    // corre, ver arriba): un bloque con `bg_animation` se quedaría sin su `.bg-layer` hasta el
    // siguiente `renderPage`. Por defecto, para él el repintado completo es el único correcto.
    //
    // `opciones.rehaceFondoAnimado` = «yo me encargo del fondo después de que repintes». Lo pasa
    // SOLO la previsualización efímera (`utils/preview-efimero.js`), que a continuación llama a
    // `initBackgroundAnimations(wrapper)` acotado a este bloque. Por qué hacía falta: al rendirse
    // aquí se caía al `renderPage` completo, que es justo el camino que destruye todos los fondos
    // canvas de la página y sustituye elementos entre `mousedown` y `mouseup` — el parpadeo del
    // menú de variantes y el «se ve la preview pero al hacer clic no se pone». Sin la opción,
    // el comportamiento es BYTE A BYTE el de antes (lo usa `dimension-editor`, que no rehace nada).
    const anim = block.props && block.props.bg_animation;
    if (anim && anim !== 'none' && !(opciones && opciones.rehaceFondoAnimado)) return false;

    try {
        const siteData = store.get('siteData');
        const tokens = (siteData && siteData.site_tokens) || {};
        // Jerarquía de titulares: es estado de PÁGINA. En vez de recalcular la cascada de
        // los bloques anteriores, se DERIVA del DOM vivo — ¿hay ya un <h1> fuera de este
        // wrapper? Así el bloque repintado no se queda sin h1 ni crea un segundo.
        const yaHayH1 = Array.prototype.slice.call(zona.querySelectorAll('h1'))
            .some(h => !viejo.contains(h));

        const html = construirBloqueHtml(block, index, blocks, {
            tokens, tema: temaDeTokens(tokens), estado: { yaHayH1 },
        });
        if (!html) return false;

        const tpl = document.createElement('template');
        tpl.innerHTML = html.trim();
        const nuevo = tpl.content.firstElementChild;
        if (!nuevo) return false;
        viejo.replaceWith(nuevo);
        // Botones sin destino del bloque repintado: la misma pasada que `renderPage` (4c).
        aplicarDestinosDeBotones(nuevo, contextoDeBotones(siteData, store.get('currentPageId'), blocks, getRenderLang()));

        // El hueco bajo el header fijo lo escribe `applyFirstSectionHeaderOffset` INLINE en la
        // primera sección de la página: si la que se acaba de sustituir era ésa, el titular se
        // metía debajo del header. Es idempotente y barato: se reescribe siempre.
        applyFirstSectionHeaderOffset();

        // Atributos de tema por <section>. Sobre la ZONA entera y no solo sobre el bloque
        // nuevo porque el preset de CONTRAPUNTO se reparte por posición y necesita ver
        // todas las secciones. Es barato y no reconstruye markup (solo atributos y custom
        // properties vía API DOM) → no reintroduce el flash.
        applyTokensOnly(tokens, { root: zona });

        // REVELAR LAS IMÁGENES. No es un hook post-render (los hooks se omiten a
        // propósito, ver arriba): `renderPage` lo llama aparte y aquí hace la misma falta.
        // Las <img> nacen con opacity:0 para el fundido, así que sin esto la sección
        // repintada se queda con el "Cargando activo visual…" para siempre — que es justo
        // el bug que el propio unsplash.js documenta en su comentario del early-exit.
        // Es idempotente y NO llama a la API cuando la query ya es una URL resuelta
        // (isResolved corta antes del fetch), así que en una previsualización no cuesta nada.
        if (typeof window.resolverImagenesUnsplash === 'function') {
            window.resolverImagenesUnsplash();
        }

        if (!store.get('editorMode')) {
            nuevo.querySelectorAll('[contenteditable="true"]')
                 .forEach(el => el.setAttribute('contenteditable', 'false'));
        }
        return true;
    } catch (e) {
        console.warn('[render] repintarBloque no pudo; se cae a renderPage:', e);
        return false;
    }
}


// SEPARADOR DE SECCIÓN — el catálogo de formas (`DIVIDER_CATALOG`), `getDividerPaths` y la
// blocklist `DIVIDER_EXCLUDE_TYPES` VIVEN en `core/dividers.js`, un módulo PURO, por lo mismo que
// los diales de tema viven en `core/theme-tokens.js`: render.js no es importable fuera del
// navegador (store.js + DOM) y esa lógica —qué separador hay entre dos secciones y cómo se
// pinta— necesita tests de node y la comparten el editor y el publicado. Se re-exportan para no
// cambiarle la API a nadie: divider-motion.js, divider-studio.js, tools.js y viewer.js los
// siguen importando de aquí (o, mejor, del módulo puro).
export { DIVIDER_CATALOG, getDividerPaths, DIVIDER_EXCLUDE_TYPES, RECORTE_ARRIBA_EXCLUDE } from './dividers.js';


/**
 * Genera un SVG inline decorativo a partir de la config canvas_shape.
 * @param {Object} shape - { preset, points, color, opacity, blur, scale, rotation, position }
 *   `points` es OPCIONAL: si falta (la IA solo elige el NOMBRE del preset — ver Pass 2 en
 *   llm.py y el clamp llm_sanitizers._fix_canvas_shape), la geometría y los defaults de
 *   blur/opacidad/escala se resuelven aquí desde SHAPE_PRESETS. Así el catálogo de formas
 *   sigue teniendo UNA sola fuente de verdad (JS) y el modelo no inventa curvas bézier.
 * @returns {string} HTML del SVG posicionado absolutamente
 */
export function buildCanvasShapeSVG(shape) {
    const preset = (shape && shape.preset && SHAPE_PRESETS[shape.preset]) || null;
    const { color, rotation, position } = shape;
    const points = (shape.points && shape.points.length) ? shape.points : (preset ? preset.points : null);
    const opacity = shape.opacity ?? preset?.defaultOpacity;
    const blur = shape.blur ?? preset?.defaultBlur ?? 0;
    const scale = shape.scale ?? preset?.defaultScale;
    const pathD = pointsToSVGPath(points, 100, 100);
    if (!pathD) return '';

    const uid = 'cs-blur-' + Math.random().toString(36).slice(2, 8);
    const filterDef = blur > 0
        ? `<defs><filter id="${uid}" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="${blur / 4}"/></filter></defs>`
        : '';
    const filterAttr = blur > 0 ? `filter="url(#${uid})"` : '';
    const isStroke = shape.isStroke ?? preset?.isStroke ?? false;
    const fillAttr = isStroke ? 'fill="none"' : `fill="${color || 'currentColor'}"`;
    const strokeAttr = isStroke ? `stroke="${color || 'currentColor'}" stroke-width="${shape.strokeWidth || preset?.strokeWidth || 2}"` : '';
    const clampedOpacity = Math.min(opacity ?? 8, 30) / 100;

    return `<svg class="canvas-shape-decor" viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style="position:absolute; pointer-events:none; z-index:0;
               width:${scale || 80}%; height:${scale || 80}%;
               left:${position?.x ?? 50}%; top:${position?.y ?? 50}%;
               transform: translate(-50%,-50%) rotate(${rotation ?? 0}deg);
               opacity:${clampedOpacity}; overflow:visible;">
        ${filterDef}
        <path d="${pathD}" ${fillAttr} ${strokeAttr} ${filterAttr}/>
    </svg>`;
}


// ── Hueco del header fixed sobre la 1ª sección de la página ─────────────────
// El <header> .nav-pro es position:fixed y se HOISTEA fuera de #resultado
// (render.js preview / public-init.js publicado): no ocupa sitio y la 1ª sección
// empieza DEBAJO de él. En el editor va en flujo y la sección empieza donde él
// acaba. Para que las dos superficies se vean igual, la 1ª sección SUMA lo que
// tapa el header a su propio padding-top (core/hueco-cabecera.js). Un único sitio,
// usado por AMBOS entry points. Hasta el 25-09-2026 el hero no recibía nada (su
// «reserva» de 180 px era una regla muerta) y el resto recibía `alto + 24 px`:
// 24 px de aire frente a los 100-184 del editor.

// Localiza el header YA hoisteado (fuera de #resultado). Editor-preview le pone
// id="hoisted-header"; en publicado queda como hermano previo a #resultado.
// NUNCA devuelve el header del modo EDITOR (que sigue dentro de #resultado y no
// es fixed → sin overlap → no debe recibir offset).
function findHoistedHeader() {
    const byId = document.getElementById('hoisted-header');
    if (byId) return byId;
    const root = document.getElementById('resultado');
    if (!root) return null;
    const prev = root.previousElementSibling;
    if (prev && prev.matches && prev.matches('header.nav-pro')) return prev;
    const any = document.querySelector('header.nav-pro');
    return (any && !root.contains(any)) ? any : null;
}

let _headerOffsetResizeBound = false;
let _roCabecera = null;
let _cabeceraVigilada = null;

/**
 * Re-mide cuando cambia el TAMAÑO del header (carga tardía del logo o de la fuente, una línea
 * más…). Un solo ResizeObserver, re-apuntado al header vivo: cada `renderPage` hoistea uno nuevo.
 */
function vigilarCabecera(header) {
    if (typeof ResizeObserver !== 'function' || header === _cabeceraVigilada) return;
    if (!_roCabecera) {
        let raf = 0;
        _roCabecera = new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => applyFirstSectionHeaderOffset());
        });
    }
    _roCabecera.disconnect();
    _roCabecera.observe(header);
    _cabeceraVigilada = header;
}

/**
 * Hace sitio al header fixed en la 1ª sección de la página: su padding-top pasa a ser el
 * PROPIO + lo que tapa el header (core/hueco-cabecera.js). Idempotente y re-ejecutable
 * (resize, cambio de tamaño del header, mobile-toggle): el propio se mide una sola vez.
 * @param {HTMLElement} [headerEl] - Header ya hoisteado; si falta, se resuelve.
 */
export function applyFirstSectionHeaderOffset(headerEl) {
    const root = document.getElementById('resultado');
    if (!root) return;
    const header = headerEl || findHoistedHeader();
    if (!header) {
        // SIN header hay que LIMPIAR `--header-h`, no solo dejar de escribirla: es una custom
        // property de <html>, sobrevive a la navegación SPA del editor/viewer, y una página sin
        // cabecera se quedaba con la altura de la anterior → `html { scroll-padding-top }`
        // reservaba hueco para un header que ya no existe (un salto a un ancla aterrizaba con
        // un espacio en blanco encima). Escribir '0px' es exacto: la altura real es cero.
        document.documentElement.style.setProperty('--header-h', '0px');
        return;
    }

    // Lo que TAPA el header: su borde inferior respecto al de #resultado (en la píldora, 20 px
    // más que su alto; ver hueco-cabecera.js).
    const reserva = reservaDeCabecera(header, root);
    // `--header-h` = eso mismo, publicado como custom property de la RAÍZ. Se escribe SIEMPRE que
    // haya header, aunque no haya nada que corregir debajo (por eso va antes de los
    // early-returns): es un dato del SITIO, no de esta corrección concreta. Lo consumen el CSS
    // (`html { scroll-padding-top }`, para que un salto a un ancla no aterrice DEBAJO del header;
    // el panel pegado de timeline v4 y el de journey v2) y `timeline.js`. Hasta el 25-09-2026 era
    // `offsetHeight`, que en la píldora se quedaba 20 px corto.
    document.documentElement.style.setProperty('--header-h', reserva + 'px');   // 0 incluido: nunca un valor viejo

    // Re-medida cuando cambia el header o la ventana. Se instalan AQUÍ, antes de los
    // early-returns: `--header-h` debe seguir al header aunque la 1ª sección sea un carrusel (que
    // no recibe hueco). Las dos son idempotentes.
    vigilarCabecera(header);
    if (!_headerOffsetResizeBound) {
        _headerOffsetResizeBound = true;
        let raf = 0;
        window.addEventListener('resize', () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => applyFirstSectionHeaderOffset());
        });
    }

    // 1er bloque en orden de documento (el header ya está FUERA de #resultado). Se busca
    // `section, footer` y NO solo `section`: una página puede quedarse sin ninguna sección
    // (borras el único bloque de contenido y quedan header + pie), y entonces el primer
    // elemento es el `<footer>` — que se quedaba DEBAJO del header fijo porque esta función
    // devolvía sin hacer nada. Bug reportado en producción.
    const firstSection = root.querySelector('section, footer');
    // El carrusel y la imagen reservan su hueco en su propio CSS (son a sangre: un padding les
    // abriría una banda de color sobre la foto) → no duplicar. La lista es SSOT
    // (CLASES_RESERVAN_HEADER), no un literal suelto.
    if (!firstSection) return;
    if (CLASES_RESERVAN_HEADER.some(c => firstSection.classList.contains(c))) return;

    // Padding propio (el del token, el de `data-spacing` o el `padding` en línea del bloque) +
    // lo que tapa el header, en línea y !important. Mismo sitio para el hero y para las
    // subpáginas: el texto queda a la distancia del header que se ve en el editor.
    reservarHueco(firstSection, reserva);
}

// ── LANDMARKS DE ACCESIBILIDAD DEL SITIO SERVIDO (<main> + skip-link) ────────
// El HTML del builder solo emite <section>/<header>/<footer>: no hay un <main>, así que un
// lector de pantalla no tiene "contenido principal" al que saltar y quien navega con teclado
// tiene que tabular el menú entero en CADA página. Se resuelve en las DOS superficies servidas
// con UNA sola función (DRY):
//   · publicado estático  → js/public/public-init.js
//   · SPA en vivo/preview → aquí mismo, al final de renderPage cuando NO es el canvas del editor
//     (views.py::_render_builder_public sirve el borrador así, sin HTML compilado).
// Se hace en RUNTIME y no horneándolo en compileSiteHtml a propósito: así también arreglan los
// sitios YA publicados (que rehidratan con este mismo bundle) sin tener que republicarlos, y no
// se toca el contrato de page_html —cuyo shim de animaciones busca las <section> como hijas
// directas de su contenedor (public-init.js), y un <main> horneado se las escondería—.
// OJO al HOIST del <header> (render.js/public-init.js lo sacan de #resultado por el
// container-type que atrapa position:fixed): el <main> NO puede envolverlo, ni al <footer>.
const MAIN_ID = 'n3i-main';

/** El pie en cualquiera de sus TRES formas: <footer> a pelo, wrapper `data-type="footer"` o
 *  wrapper que lo contiene (editor y shim público). Una sola definición: la usan el filtro de
 *  landmarks y el ancla del <main> vacío, que antes la copiaban. */
function esFooter(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.matches && el.matches('footer')) return true;
    if (el.getAttribute && el.getAttribute('data-type') === 'footer') return true;
    return !!(el.querySelector && el.querySelector(':scope > footer'));
}

function esLandmarkFuera(el) {
    if (!el || el.nodeType !== 1) return true;
    if (esFooter(el)) return true;
    // Cualquier <main> queda fuera, no solo el nuestro: el HTML publicado trae uno horneado por
    // `compileSiteHtml` (con id 'contenido'), y meterlo dentro de otro <main> anida dos landmarks.
    if (el.matches && el.matches('main, header')) return true;
    if (el.classList && el.classList.contains('n3i-skip-link')) return true;
    if (el.getAttribute && el.getAttribute('data-type') === 'header') return true;
    // Wrapper de bloque que contiene el header (editor y shim público).
    return !!(el.querySelector && el.querySelector(':scope > header'));
}

/**
 * Envuelve el contenido servido en un <main> y antepone un enlace "saltar al contenido".
 * Idempotente (se puede llamar en cada render) y defensiva: si algo falta, no hace nada.
 * @param {string} [etiqueta] - Texto del skip-link (el caller lo trae ya localizado).
 */
export function applyPublicLandmarks(etiqueta) {
    if (typeof document === 'undefined') return;
    const root = document.getElementById('resultado');
    if (!root) return;
    // Anfitrión del contenido: en el publicado las secciones cuelgan de #nube3i_content_block.
    const host = root.querySelector('#nube3i_content_block') || root;

    // ADOPTAR antes que crear. En el sitio publicado ya hay un <main id="contenido"> HORNEADO por
    // `compileSiteHtml`, y este código corre igualmente al rehidratar. Creando el nuestro sin mirar,
    // el horneado acababa DENTRO — dos <main> anidados (landmark inválido) y el skip-link apuntando
    // a un destino sin `tabindex`, así que el salto de foco no llegaba a funcionar.
    let main = host.querySelector(':scope > main') || document.getElementById(MAIN_ID);
    if (!main || !host.contains(main)) {
        const hijos = Array.from(host.children).filter((el) => el.id !== MAIN_ID);
        const primero = hijos.find((el) => !esLandmarkFuera(el));
        if (main && main.parentNode) main.parentNode.removeChild(main);
        main = document.createElement('main');
        main.id = MAIN_ID;
        // El <main> se crea AUNQUE no haya nada que envolver (una web recién empezada, con solo
        // cabecera y pie). Antes se hacía `return` y eso rompía el pie pegado al fondo: el
        // sticky-footer de site.css reparte el alto sobrante con `> main { flex: 1 0 auto }`, o
        // sea que SIN <main> el pie era el único item del flex, nadie crecía y quedaba pegado
        // bajo la cabecera con el resto del viewport al aire. Vacío también sirve de destino al
        // skip-link. Si no hay contenido, va DELANTE del pie para no alterar el orden de lectura.
        const ancla = primero || hijos.find(esFooter) || null;
        if (ancla) host.insertBefore(main, ancla);
        else host.appendChild(main);
        hijos.forEach((el) => { if (!esLandmarkFuera(el)) main.appendChild(el); });
    }
    if (!main.id) main.id = MAIN_ID;                // horneado sin id: se lo damos para enlazarlo
    main.setAttribute('tabindex', '-1');            // destino focusable del skip-link

    // El enlace apunta al <main> que haya QUEDADO (el horneado o el nuestro) y se sube al principio
    // del <body>: el horneado vive dentro del bloque de contenido, o sea DESPUÉS del menú, y un
    // "saltar al contenido" que no es lo primero que se tabula no sirve para lo único que sirve.
    let a = document.querySelector('.n3i-skip-link');
    if (!a) {
        a = document.createElement('a');
        a.className = 'n3i-skip-link';
        a.textContent = etiqueta || 'Saltar al contenido';
    }
    a.href = '#' + main.id;
    if (document.body.firstChild !== a) document.body.insertBefore(a, document.body.firstChild);
}

/**
 * Construye el HTML del wrapper de UN bloque (`.block-editor-wrapper` + su <section>
 * con TODAS las inyecciones: tokens de sección, --secondary, data-bg-anim,
 * data-spacing/content-width, canvas-shape y divider).
 *
 * Extraída del bucle de `renderPage` SIN cambiar una coma, para que exista un único
 * constructor de sección y `repintarBloque` no abra un segundo camino que se
 * desincronice (que es exactamente el gotcha nº12 del CODEBASE_SUMMARY).
 *
 * @param {Object} block   bloque {type, props}
 * @param {number} index   su índice en la página
 * @param {Array}  blocks  la página entera (hace falta: el divider mira al SIGUIENTE)
 * @param {Object} ctx     { tokens, tema, estado } — `estado.yaHayH1` es MUTABLE:
 *                         la jerarquía de titulares es estado de PÁGINA, no de bloque.
 * @returns {string} HTML del wrapper, o '' si el tipo no está en el registry.
 */
/**
 * Lo que se pinta cuando el renderer de un bloque LANZA.
 *
 * POR QUÉ EXISTE: `renderPage` acumula el HTML de TODOS los bloques en un string y solo entonces
 * lo asigna a `innerHTML`. Sin esta red, un único renderer que reviente deja la página **entera**
 * sin pintar — no su bloque, la página. Y revienta con facilidad, porque los props los escribe el
 * LLM: bastaba un `gallery_items: [null]` para que un `item.image.startsWith` tumbara la web recién
 * generada. Ese es el fallo intermitente de "recuadros en blanco" que nadie conseguía reproducir:
 * dependía de lo que devolviera el modelo.
 *
 * Se emite una `<section>` con el FONDO del propio bloque para que el ritmo de la página no se
 * rompa, y el aviso solo se ve en el editor: al visitante de la web publicada no se le cuentan
 * nuestros errores, pero al cliente que está editando hay que decirle qué sección va mal para que
 * pueda borrarla o rehacerla.
 */
function bloqueRoto(block, index, error) {
    console.error(`[render] el bloque "${block && block.type}" (índice ${index}) no se pudo pintar:`, error);
    // El color va a un `style=` y NO ha pasado por `safe()` (esta rama se pinta justo cuando
    // el render del bloque ha fallado): se escapa como en cualquier otro atributo.
    const bg = escapeHtml((block && block.props && block.props.bgColor) || 'transparent');
    const aviso = store.get('editorMode')
        ? `<div class="editor-only" style="max-width:640px;margin:0 auto;padding:18px 22px;border:1px solid #f2d6a6;background:#fdf2e2;color:#b3640b;border-radius:12px;font:500 14px/1.5 system-ui,sans-serif;text-align:center;">
               Esta sección (<b>${String(block && block.type || '?').replace(/[<&]/g, '')}</b>) no se ha podido dibujar.
               Bórrala o vuelve a generarla; el resto de la web no se ve afectada.
           </div>`
        : '';
    return `<section id="bloque-roto-${index}" style="--bg:${bg}; background:${bg}; padding: var(--section-pad-y, 60px) 8%;">${aviso}</section>`;
}

export function construirBloqueHtml(block, index, blocks, ctx) {
    if (!blockRegistry[block.type]) return '';
    ctx = ctx || {};
    ctx.tokens = ctx.tokens || {};
    ctx.tema = ctx.tema || {};
    ctx.estado = ctx.estado || { yaHayH1: false };

          // Solo el MARCO es no-eliminable (el porqué de que el hero YA NO lo sea, en
          // blocks/block-meta.js::protected, su espejo). Gobierna también el drag-handle.
          const isProtected = ['header', 'footer'].includes(block.type);
          const hasVariants = getVariantIds(block.type).length > 1;
          const isHeaderV3 = block.type === 'header' && block.props?.variant === 'v3';
          let wrapperStyle = (isHeaderV3 && store.get('editorMode')) ? 'min-height: 80px;' : '';
          
          // Sincronizar fondo del bloque con el wrapper exterior para evitar gaps transparentes
          if (block.props?.bgColor) {
              wrapperStyle += ` --bg: ${escapeHtml(block.props.bgColor)}; background-color: var(--bg);`;
          }

          // RECORTE del separador: el wrapper lleva LOS MISMOS `data-recorte` + variables que la
          // <section> de dentro, y es él quien recibe el clip y el margen negativo (en site.css
          // manda el de FUERA: `[data-recorte] > [data-recorte]` anula los de la sección).
          // Tiene que ser así porque el wrapper pinta el fondo del bloque (`background-color:
          // var(--bg)`) y gana z-index al pasar el ratón: sin recortarlo, taparía la cuña que acaba
          // de abrir la sección. Fuera del editor no hay wrapper y el clip lo lleva la <section>.
          // Se decide UNA vez por bloque (dos vecinas) y se le pasa a `inyectarDivider`, que si
          // no lo recalcularía; y se decide protegido: si fallara, el wrapper no puede quedarse
          // con un `data-recorte` (margen negativo incluido) que la <section> no va a tener.
          // Si `separadoresDe` falla se sigue SIN separador (`SIN_SEPARADORES`), NO con `null`:
          // `inyectarDivider` trata un `sep` vacío como "calcúlalo tú" (dividers.js:649) y volvía
          // a llamar a la función que acababa de lanzar, así que el bloque terminaba pintado como
          // TARJETA DE ERROR en vez de quedarse simplemente sin su separador, que es lo que este
          // try/catch prometía.
          // Las VECINAS son las de lo publicado cuando la vista lo es (`ctx.vecinas`, de renderPage
          // fuera del editor: utils/publicable.js quita la tira de logos vacía). El `index` sigue
          // siendo el del bloque en la página: da el `data-index` del extractor y algunos ids.
          const vecinas = ctx.vecinas || blocks;
          const pos = ctx.vecinas ? ctx.vecinas.indexOf(block) : index;
          let separadores = null;
          try { separadores = separadoresDe(vecinas, pos); } catch (e) { separadores = SIN_SEPARADORES; }
          const recorte = separadores ? separadores.recorte : null;
          if (recorte) wrapperStyle += ` ${recorte.estilo}`;

          const hasImage = block.props && block.props.use_image === true;
          // Gobierna el botón genérico de QUITAR imagen. Ojo: es una de TRES listas
          // independientes sobre "este bloque lleva imagen" (las otras: `tiposConImagen` en
          // editor/catalog.js, que decide si se abre el picker, y `BLOCK_META.supportsImage`,
          // que no lee ningún JS y solo viaja al schema de la IA). No están unificadas: al dar
          // de alta un bloque con imagen hay que tocar esta y la de catalog.js.
          // OJO: `imagen` NO entra aquí, y no es un olvido. Este botón hace
          // `use_image=false; image_url=''; image_keyword=''`, pensado para bloques donde la
          // foto es un ADORNO opcional. En un bloque cuyo contenido ES la imagen deja la
          // sección con un placehold.co gris permanente y, al pasar `hasImage` a false, se
          // esconde a sí mismo: callejón sin salida. Ahí lo que se quita es el bloque entero.
          const supportsImage = ['hero', 'content', 'features'].includes(block.type);
          // data-anim="" (NO "none") cuando el bloque no fija props.animation: "sin
          // preferencia" y "el cliente ha elegido Ninguna" TIENEN que ser distinguibles
          // en el DOM. anime-orchestrador.js honra 'none' ANTES de la coreografía de
          // variante (para que "Ninguna" pueda desactivarla), así que si el defecto
          // siguiera siendo 'none' TODOS los bloques sin prop se quedarían sin animación
          // en ver_sitio (que corre con IS_PUBLIC → editorMode:false → sí anima).
          // El atributo se emite SIEMPRE (aunque vacío): el orquestador selecciona por
          // [data-anim] y el CSS oculta `[data-anim]:not(.anime-init) > section`.
          // SEGURIDAD (PDR-S1): estos valores salen de `block.props` —o sea, de
          // `datos_json_builder`, que entra por la API— y se interpolan como VALOR DE
          // ATRIBUTO. Un `"` cerraría el atributo y colaría un `onmouseover=` en el EDITOR,
          // que corre en el MISMO origen que el ERP. `safe()` no ayuda aquí: normaliza una
          // COPIA de los props que usan los renderers, y este markup lo arma `render.js` con
          // `block.props` en crudo. `viewer.js` (su gemelo publicado) ya escapaba; esto cierra
          // la asimetría. La frontera de escritura solo valida los CINCO props de color
          // (`views.py::_sanitizar_props_css`); `animation`, `spacing` y `content_width` no.
          const animValue = block.props?.animation || '';
          const isAiCustom = block.type === 'ai-custom';

          // BARRA DEL BLOQUE: [título] | IA | Diseño | Organizar (utils/barra-bloque.js). Los botones
          // de base nacen ya en su grupo; los que inyecta editor/tools.js los coloca `agruparBarra`.
          // Quién tiene cada botón sigue siendo la regla de siempre. Novedades del diseño: la
          // cabecera y el pie solo tienen «Mejorar» en IA (sin Variaciones), y «Duplicar» sale en
          // todo menos en cabecera, portada, pie y bloque IA. El chrome no se publica: compileSiteHtml
          // borra `.block-tools` y `.add-block-btn` enteros.
          const boton = (clase, opciones) => {
              const e = especDeClase(clase);
              const icono = (opciones && opciones.icono) || e.icono;
              return htmlBotonBarra(e, ph(icono, 17), opciones);
          };
          const grupoBarra = (grupo, botones) => (botones.join('') ? `<div class="n3i-tb__group" data-grupo="${escapeHtml(grupo)}">${botones.join('')}</div>` : '');
          const nombreSeccion = nombreDeBloque(block.type, (getBlockMeta(block.type) || {}).label);
          const puedeDuplicar = !isProtected && !isAiCustom && block.type !== 'hero';
          const barraIA = isAiCustom
              ? [boton('ai-improve-btn'), boton('ai-regenerate-btn'), boton('ai-code-btn')]
              : [boton('ai-btn'), isProtected ? '' : boton('variations-btn')];
          const barraDiseno = isAiCustom ? [] : [
              hasVariants ? boton('variant-btn') : '',
              boton('image-picker-btn'),
              // «Estilo de la imagen» (D-1429): tarjeta o recorte, donde la foto va en una caja.
              admiteRecorte(block.type, block.props) ? boton('image-style-btn', { pulsado: modoImagen(block.props).recorte }) : '',
              supportsImage && hasImage ? boton('image-remove-btn') : '',
          ];
          // «Añadir al menú» (utils/ancla-seccion.js): la sección se enlaza desde la cabecera de su
          // página. Solo en tipos que pintan una <section> con id y si la página tiene cabecera.
          const puedeIrAlMenu = !!idDeSeccion(block.type, index) && Array.isArray(blocks) && blocks.some(b => b && b.type === 'header');
          const enElMenu = puedeIrAlMenu && estaEnLaCabecera(blocks, index);
          const barraOrg = [
              !isProtected ? boton('drag-handle') : '',
              puedeIrAlMenu ? boton('menu-ancla-btn', { pulsado: enElMenu, tip: enElMenu ? 'Quitar del menú' : 'Añadir al menú' }) : '',
              puedeDuplicar ? boton('duplicate-btn') : '',
              isProtected ? boton('delete-btn', { tip: TIP_PROTEGIDA, icono: 'lock-simple', disabled: true }) : boton('delete-btn'),
          ];

          return `
          <div class="block-editor-wrapper" data-index="${index}" data-type="${escapeHtml(block.type)}" data-anim="${escapeHtml(animValue)}"${recorte ? ` data-recorte="${escapeHtml(recorte.atributo)}"` : ''} style="${wrapperStyle}">
              <div class="block-tools" role="toolbar" aria-label="Herramientas de la sección ${escapeHtml(nombreSeccion)}">
                  <div class="n3i-tb__title"><span class="n3i-tb__nombre">${escapeHtml(nombreSeccion)}</span></div>
                  ${grupoBarra('ia', barraIA)}${grupoBarra('diseno', barraDiseno)}${grupoBarra('org', barraOrg)}
              </div>

              ${(() => {
                // AISLAMIENTO POR BLOQUE — ver `bloqueRoto`. El cuerpo se deja con su sangría
                // original a propósito: envolverlo en `try` sin re-indentar 120 líneas mantiene el
                // diff legible y no cambia ni una coma de la lógica.
                try {
                  // La variant va sin escapar al class del <section>: solo identificadores limpios.
                  let blockHtml = blockRegistry[block.type](propsConVarianteSegura(block.type, block.props), index);

                  // Jerarquía de titulares decidida a nivel de PÁGINA (un solo <h1>):
                  // el primer bloque titular se lo queda, los demás bajan a <h2>, y si
                  // la página no tiene hero/video el primer título de sección sube a
                  // <h1>. PARIDAD OBLIGADA en compileSiteHtml (gotcha #12).
                  const _jt = jerarquiaTitulares(blockHtml, block.type, ctx.estado.yaHayH1);
                  blockHtml = _jt.html;
                  if (_jt.tomaH1) ctx.estado.yaHayH1 = true;

                  // Espaciado global (--section-pad-y, slider "Espaciado" del Estudio de Tema).
                  // Los bloques emiten su padding vertical INLINE (p.ej. `padding: 100px 8%`),
                  // que gana a cualquier regla CSS → el token solo funciona si se enhebra DENTRO
                  // del inline, conservando el valor propio del bloque como fallback (sin token,
                  // cada bloque mantiene su ritmo original: 80/100/120px). Se hace aquí y no en
                  // los ~25 templates de bloque (un solo punto + cubre bloques futuros). OJO: el
                  // token es NUEVO a propósito — el legado --section-gap lo escribe la IA en
                  // site_tokens pero nunca gobernó el layout real; reutilizarlo re-tramaría de
                  // golpe todos los sitios ya generados. Réplica OBLIGADA en compileSiteHtml
                  // (gotcha #12). ai-custom queda fuera (su padding es chrome del placeholder).
                  // ([^>]*? y no [\s\S]*?: obliga a que el padding esté en el style del PROPIO
                  // tag <section> — sin ello, en bloques cuyo section no trae padding (hero) el
                  // regex saltaría el cierre del tag y reescribiría el de un div interior.)
                  if (block.type !== 'ai-custom') {
                      blockHtml = blockHtml.replace(
                          /(<section\b[^>]*?style="[^"]*?)padding:\s*(\d+)px/,
                          '$1padding: var(--section-pad-y, $2px)'
                      );
                  }

                  // Sombras del Estudio de Tema DENTRO de la <section>. Los tokens de sitio se
                  // declaran en #resultado, pero su tinte es `var(--text)` y --text NO existe
                  // ahí (vive inline en cada sección) → allí quedan guaranteed-invalid y toda
                  // regla cae a su fallback. Re-declarándolos en la sección, `var(--text)`
                  // resuelve con el color de ESA sección: sombra oscura en secciones claras y
                  // glow claro en las oscuras, sin recuadros. Réplica OBLIGADA en
                  // compileSiteHtml (gotcha #12).
                  //
                  // SEGURIDAD (allowlist obligatoria): site_tokens llega de datos_json_builder,
                  // que se guarda vía API SIN validar valores — y esta es la ÚNICA ruta donde un
                  // token se interpola como MARKUP (el setProperty de applyTokensOnly es API DOM,
                  // inmune). Sin la allowlist, un valor con `"` rompería el atributo style e
                  // inyectaría atributos/HTML en el editor (XSS almacenado intra-tenant; el
                  // publicado lo mitiga el sanitizer, el editor no tiene). `cssTokenSafe` es la
                  // MISMA función que usa compileSiteHtml (importada de esta sección).
                  //
                  // `--title-len` viaja por el mismo canal (nº de caracteres del titular, entero
                  // ya saneado por longitudTitular) para que --title-adj pueda escalar el <h1>
                  // según lo que MIDE el texto. PARIDAD en compileSiteHtml (gotcha #12).
                  const _len = longitudTitular(block.props);
                  const shTokens = SECTION_TOKENS
                      .filter(k => ctx.tokens[k] && cssTokenSafe(ctx.tokens[k]))
                      .map(k => `${k}:${ctx.tokens[k]};`).join('')
                      + (_len ? `--title-len:${_len};` : '');
                  if (shTokens) {
                      blockHtml = blockHtml.replace(/(<section\b[^>]*?style=")/, `$1${shTokens}`);
                  }

                  // DUOTONO DE MARCA: --secondary DENTRO del tag raíz del bloque, junto a --accent.
                  // El segundo color de marca se calcula, se protege y se persiste desde hace tiempo
                  // (props.secondaryColor), pero no lo pintaba nadie: el CSS lo consume ahora como
                  // `var(--secondary, var(--accent))` en los puntos NO-CTA (kickers, badges, filos,
                  // puntos de timeline…). Se inyecta aquí y no en los ~25 templates de bloque por lo
                  // mismo que --section-pad-y: un solo punto y cubre los bloques futuros.
                  // Se emite SOLO si el bloque define un secundario válido → un sitio sin él queda
                  // byte-idéntico (ni atributo nuevo ni var declarada; el CSS cae al accent).
                  // header/footer entran también (emiten <header>/<footer>, no <section>) porque su
                  // marca de color es la misma. Réplica OBLIGADA en compileSiteHtml (gotcha #12).
                  const secColor = secondaryOf(block.props || {});
                  if (secColor) {
                      blockHtml = blockHtml.replace(
                          /(<(?:section|header|footer)\b[^>]*?style=")/,
                          `$1--secondary:${secColor};`
                      );
                  }

                  // «TEXTO DE LA SECCIÓN» (F4 del plan de personalización): fuente y los dos tamaños de
                  // ESTA sección como variables en su <section> (utils/texto-seccion.js, la MISMA llamada
                  // que compileSiteHtml: gotcha #12). Sin props no escribe nada. La fuente se descarga
                  // aquí; en el publicado la pide el <head> (`enlacesDeFuentes`).
                  blockHtml = inyectarTextoSeccion(blockHtml, block);
                  const fuenteSec = fuenteSegura(block.props && block.props.fuente);
                  if (fuenteSec) cargarFuentesDePreset(fuenteSec);

                  // ESTILO DEL TITULAR (F6, utils/titulo-estilo.js): degradado y palabras resaltadas del título de
                  // la sección, en su <section> (la portada lo pinta su render). La MISMA llamada que
                  // compileSiteHtml (gotcha #12); sin nada puesto no escribe nada. La itálica de una palabra en
                  // cursiva se descarga aquí; en el publicado la pide el <head> (`enlacesDeCursiva`).
                  blockHtml = inyectarTituloSeccion(blockHtml, block);
                  if (resaltesDe(block.props && block.props.title).indexOf('cursiva') !== -1) {
                      cargarHojaDeFuente(urlDeCursiva(serifDeCursiva(block.props)));
                  }

                  // Fondo animado (data-bg-anim + config) en la <section>: TODO —si lo lleva, si la
                  // capacidad del bloque lo admite, y los atributos— vive en
                  // `animations/fondos/inyectar.js::inyectarFondo`, que llama TAMBIÉN
                  // compileSiteHtml: paridad editor/publicado por construcción (gotcha #12), como
                  // el separador. Y busca el cierre del tag respetando las comillas.
                  blockHtml = inyectarFondo(blockHtml, block);

                  // Knobs "Ajustes de sección" (C3): espaciado vertical + ancho del
                  // contenido como data-* en la <section>; el CSS hace el override.
                  // El ancho por DENSIDAD solo rellena el hueco: el ajuste por bloque
                  // (elección explícita del cliente) siempre manda.
                  const spacing = block.props?.spacing;
                  const contentWidth = block.props?.content_width
                      || anchoPorDensidad(ctx.tema.density, block.type, block.props);
                  // (Estas dos y las de compileSiteHtml buscan el cierre del tag con `utils/tag.js`,
                  // respetando comillas: con la regex de antes, un `>` dentro de un atributo las
                  // metía en mitad del tag. BAJO-3 de la revisión del P2 de fondos.)
                  if (spacing || contentWidth) {
                      blockHtml = anadirAtributos(blockHtml, RE_SECCION,
                          `${spacing ? ` data-spacing="${escapeHtml(spacing)}"` : ''}${contentWidth ? ` data-content-width="${escapeHtml(contentWidth)}"` : ''}`);
                  }

                  // Inyectar Canvas Shape decorativo (Fase 2). El guard acepta también la
                  // forma que llega con SOLO el preset (la que asigna la IA en Pass 2).
                  const canvasShape = block.props?.canvas_shape;
                  if (tieneFormaValida(canvasShape)) {
                      blockHtml = insertarTrasTag(blockHtml, RE_SECCION, buildCanvasShapeSVG(canvasShape));
                  }

                  // ANCLA PROPIA de la sección (`props.ancla`, utils/ancla-seccion.js): el destino de un
                  // enlace «Sección de esta página» o «Añadir al menú». Un <span id> vacío dentro de la
                  // <section> (el id de la sección no se toca: lo usan site.css y el runtime). La MISMA
                  // llamada que compileSiteHtml (gotcha #12).
                  blockHtml = inyectarAncla(blockHtml, anclaPropia(blocks, index));

                  // Separador de sección (Fase E2). TODO —si lo hay, si es recorte o parche, y el
                  // markup— vive en `core/dividers.js::inyectarDivider`, que llama TAMBIÉN
                  // compileSiteHtml: la paridad editor/publicado (gotcha #12) es por construcción,
                  // ya no hay dos bloques gemelos que se puedan desincronizar. Va la ÚLTIMA de las
                  // inyecciones porque el modo recorte funde sus variables en el `style=` de la
                  // <section> que las anteriores ya han montado.
                  blockHtml = inyectarDivider(blockHtml, vecinas, pos, separadores || SIN_SEPARADORES);
                  return blockHtml;
                } catch (e) {
                  // El bloque de repuesto también pasa por el separador: el wrapper YA lleva
                  // `data-recorte` + margen negativo, y sin el hueco en su <section> el aviso de
                  // error solaparía H px a la sección anterior.
                  try { return inyectarDivider(bloqueRoto(block, index, e), vecinas, pos, separadores || SIN_SEPARADORES); }
                  catch (e2) { return bloqueRoto(block, index, e); }
                }
              })()}

              ${/* El header SÍ lleva "+": es el único sitio desde el que se puede insertar una
                    sección en la POSICIÓN 1 (el botón inserta en `index+1`, así que el del hero
                    ya empieza en la 2). Sin él no había forma de meter nada entre el encabezado
                    y el bloque de apertura salvo crearlo abajo y arrastrarlo.
                    Funciona con el mecanismo de siempre porque en el EDITOR el header está en
                    FLUJO y su wrapper tiene altura real: el `position:fixed` de `.nav-pro` solo
                    se aplica en `.preview-mode` (site.css), y allí el wrapper ni existe — el
                    header se hoistea fuera de #resultado y el wrapper se elimina.
                    El footer sigue sin "+": insertar DESPUÉS del pie no lleva a ningún sitio. */''}
              ${block.type !== 'footer' ? `<button type="button" class="add-block-btn" aria-label="Añadir una sección después de ${escapeHtml(nombreSeccion)}">${ph('plus', 14)}<span>Añadir sección</span></button>` : ''}
          </div>`;
}

export function renderPage(blocks) {
  const zonaResultado = document.getElementById("resultado");
  if (!zonaResultado) return;
  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) { zonaResultado.innerHTML = `<h2>Error: No hay datos.</h2>`; return; }
  
  // NOTA: renderPage NO muta window.siteBlocks.
  // El caller es el único responsable de gestionar window.siteBlocks
  // para evitar doble-clone y desincronización de referencias.

  // 1. Aplicamos o quitamos la clase de editor al contenedor principal y al body
  if (store.get('editorMode')) {
      zonaResultado.className = "editor-active";
      document.body.classList.add("editor-active");
      document.documentElement.style.setProperty('--header-h', '0px');   // en el canvas no hay header fijo hoisteado
      document.body.classList.remove("preview-mode");
  } else {
      zonaResultado.className = "preview-mode";
      document.body.classList.remove("editor-active");
      document.body.classList.add("preview-mode");
  }

  // 2. Inyectar site_tokens como CSS custom properties globales + atributos de tema
  //    (preset / densidad / estilo de botón). Todo eso vive en applyTokensOnly, que es
  //    la ÚNICA política de tokens del proyecto (la reusa el Estudio de Tema para
  //    repintar al arrastrar un slider sin regenerar el HTML). Aquí se llama DOS veces:
  //    ahora, para la raíz, y otra vez tras pintar, cuando ya existen las <section>.
  const siteData = store.get('siteData');
  const tokens = (siteData && siteData.site_tokens) || {};
  applyTokensOnly(tokens, { root: zonaResultado });

  // El atributo en #resultado NO basta para la paridad: los presets derivan sus valores
  // de `var(--bg)` y una custom property SUSTITUYE sus var() en el ELEMENTO donde se
  // declara, no en cada descendiente. Declarado en #resultado (que no tiene --bg) se
  // resolvería una sola vez contra el fallback (#ffffff) y heredaría ese MISMO color a
  // todas las secciones; el publicado, en cambio, lleva data-preset en cada <section>
  // (que sí define --bg inline) → un --card-bg por sección. Replicarlo aquí por <section>
  // (igual que compileSiteHtml) es lo que hace que editor y publicado se vean IGUAL.
  // Se aplica tras pintar el HTML (ver más abajo, junto a zonaResultado.innerHTML).
  const tema = temaDeTokens(tokens);

  // 3. Inyectar tipografía (Presets)
  const heroBlockRender = blocks.find(b => TIPOS_HERO.indexOf(b.type) >= 0) || blocks[0];
  const fontFamilyId = heroBlockRender?.props?.fontFamily || 'inter';
  loadFontsForPreset(fontFamilyId);  // 3.4: carga dinámica de fuentes
  // La fuente de etiquetas y botones (site_tokens.labelFont) elige entre las familias del MISMO
  // preset: la URL de Google Fonts de cada preset ya las trae todas. RÉPLICA en compileSiteHtml.
  const fontVars = getFontVariables(fontFamilyId, tokens.labelFont);
  Object.entries(fontVars).forEach(([key, value]) => {
      zonaResultado.style.setProperty(key, value);
  });

  // 4. Inyectar --hero-bg para que preview-mode evite flash de body negro
  // Bloque de CABECERA de la página (hero o carrusel: TIPOS_HERO). Sin este SSOT, una
  // página que abre con carrusel se quedaba sin --hero-bg → vuelve el flash de fondo negro.
  const heroBlock = blocks.find(b => TIPOS_HERO.indexOf(b.type) >= 0);
  if (heroBlock?.props?.bgColor) {
      zonaResultado.style.setProperty('--hero-bg', heroBlock.props.bgColor);
  } else {
      // SIN bloque de cabecera hay que LIMPIAR, no solo dejar de escribir: es estilo INLINE de
      // #resultado y sobrevive a la navegación SPA. Al pasar de una home con hero oscuro a una
      // página que no abre con hero (o que solo tiene cabecera y pie), el color viejo se quedaba
      // puesto y teñía de oscuro todo el viewport que no cubre ninguna sección. Mismo criterio
      // que la limpieza de `--header-h` en applyFirstSectionHeaderOffset.
      zonaResultado.style.removeProperty('--hero-bg');
  }

  // 4b. Destino GLOBAL de los botones (siteData.cta_destino): fallback de sitio para los CTA que no
  // tienen enlace propio, resuelto en el idioma de la página que se va a pintar (el que viewer.js
  // ya fijó con setRenderLang antes de llamar aquí). No muta nada del store: deja la URL en el
  // estado de render de utils/links.js, que solo la usa para el `href` (nunca para `data-link` →
  // el fallback no se hornea en los props; ver utils/cta-destino.js). RÉPLICA OBLIGADA en
  // compileSiteHtml (viewer.js) para que el sitio PUBLICADO lleve el mismo enlace (gotcha #12).
  aplicarDestinoDeSitio(siteData && siteData.cta_destino, getRenderLang(), (siteData && siteData.idioma) || 'es');

  let html = '';

  // Un solo constructor de sección para toda la app: el mismo que usa `repintarBloque`
  // para el repintado quirúrgico, así que no hay dos caminos que puedan desincronizarse
  // (gotcha nº12). `estado` viaja en el ctx y es MUTABLE porque la jerarquía de titulares
  // (un único <h1> por página) es estado de PÁGINA que cruza bloques: cada llamada lee si
  // alguien se quedó ya el h1 y marca cuando se lo queda ella.
  // Fuera del editor («Ver sitio», la SPA en vivo, el creador) se pinta lo que se PUBLICA: una tira
  // de logos o de catálogo sin elementos no sale (utils/publicable.js, la misma regla que
  // compileSiteHtml), y los separadores se deciden con las vecinas que quedan.
  const _vistaPublica = !store.get('editorMode');
  const _ctxBloque = { tokens, tema, estado: { yaHayH1: false }, vecinas: _vistaPublica ? bloquesPublicados(blocks) : null };
  blocks.forEach((block, index) => {
      if (_vistaPublica && !sePublica(block)) return;
      html += construirBloqueHtml(block, index, blocks, _ctxBloque);
  });


  zonaResultado.innerHTML = html;

  // 4c. Botones SIN destino (ni enlace propio ni destino del sitio): llevan a donde dice su texto si
  // eso existe en la web (utils/destino-boton.js). Solo el `href`, nunca `data-link`: es estado de
  // render, como el 4b. VA ANTES del hoist de la cabecera y de los hooks (el carrusel clona sus
  // diapositivas al montarse y las copias tienen que llevar ya el destino). RÉPLICA en compileSiteHtml.
  aplicarDestinosDeBotones(zonaResultado,
      contextoDeBotones(siteData, store.get('currentPageId'), blocks, getRenderLang()));

  // PARIDAD de los atributos de tema (ver punto 2): mismo criterio que compileSiteHtml →
  // data-preset / data-density / data-btn-style van en CADA <section>, para que cada una
  // resuelva var(--bg)/var(--text)/var(--accent) con SUS propios colores. Segunda llamada
  // a applyTokensOnly: ahora que el HTML existe, decora las secciones (y reparte el preset
  // de contrapunto). Es idempotente respecto a la primera.
  applyTokensOnly(tokens, { root: zonaResultado });

  // Sincronizar fondo de #resultado con el del hero/header para evitar
  // que el fondo blanco por defecto se filtre al hacer scroll
  if (heroBlock?.props?.bgColor) {
      zonaResultado.style.background = heroBlock.props.bgColor;
  } else {
      zonaResultado.style.removeProperty('background');   // ver la nota de --hero-bg
  }

  if (typeof window.resolverImagenesUnsplash === 'function') {
      window.resolverImagenesUnsplash();
  }

  if (!store.get('editorMode')) {
      zonaResultado.querySelectorAll('[contenteditable="true"]').forEach(el => el.setAttribute('contenteditable', 'false'));

      // ── HEADER HOIST (Preview) ──
      // #resultado tiene container-type:inline-size que atrapa position:fixed.
      // En preview, movemos el <header> FUERA de #resultado para que
      // position:fixed funcione contra el viewport. Le ponemos id="hoisted-header":
      // el CSS centra el header del preview MÓVIL (frame 375px) con el selector
      // `body.mobile-view #hoisted-header` (specificity por ID vence las bases width:100%,
      // y el id existe SOLO al hoistear → independiente del orden/estado del toggle y de
      // que .preview-mode esté o no en <body>).
      const prevHeader = document.getElementById('hoisted-header');
      if (prevHeader) prevHeader.remove(); // limpiar header hoisted anterior
      const headerWrapper = zonaResultado.querySelector('[data-type="header"]');
      const headerEl = headerWrapper ? headerWrapper.querySelector('header') : null;
      if (headerEl) {
          headerEl.id = 'hoisted-header';
          zonaResultado.parentNode.insertBefore(headerEl, zonaResultado);
          headerWrapper.remove();
          // Evita que el header fixed tape la 1ª sección no-hero (subpáginas).
          applyFirstSectionHeaderOffset(headerEl);
      } else {
          // Página SIN header: se llama IGUAL, sin argumento, para que la función ponga
          // `--header-h` a 0px. Es una custom property de <html> y sobrevive a la navegación
          // SPA: si solo se llamara al hoistear, su rama de limpieza no se alcanzaría nunca
          // al cambiar de página y `html{scroll-padding-top}` seguiría reservando el hueco
          // de la cabecera anterior (justo el bug que esa rama arregla).
          applyFirstSectionHeaderOffset();
      }

      // Landmarks de accesibilidad. VA DESPUÉS del hoist: si corriera antes, el <main>
      // se tragaría el <header> (que aún cuelga de #resultado) y el skip-link llevaría
      // al menú en vez de al contenido. Solo en la superficie SERVIDA (preview / SPA
      // pública): el canvas de edición no los necesita y así no se toca su DOM.
      applyPublicLandmarks(t('skip_content'));
  } else {
      // En editor mode, limpiar header hoisted si existía
      const prevHeader = document.getElementById('hoisted-header');
      if (prevHeader) prevHeader.remove();
      // …y el skip-link de la superficie servida: el <main> desaparece con el innerHTML, así
      // que dejarlo apuntaría a un ancla inexistente dentro del canvas de edición.
      const skip = document.querySelector('.n3i-skip-link');
      if (skip) skip.remove();
  }
  
  // Un hook que lance NO puede llevarse por delante a los que van detrás. Esto corría sin red
  // mientras su gemela pública (`runPublicPostRenderHooks`, justo debajo) sí la tenía: la misma
  // lista de funciones con dos comportamientos distintos, y el frágil era el del EDITOR y el
  // CREADOR. Importa porque el orden manda: cuatro hooks corren siempre y sin filtro (countdown,
  // interactive, backgrounds, whatsapp) sobre DOM recién escrito, y detrás de ellos va
  // `initGlobalAnimations`, que es quien pone `.anime-init` — el único que levanta el
  // `opacity: 0` que `css/site.css:104` deja sobre CADA sección en preview. Es decir: cualquiera
  // de esos cuatro tropezando dejaba **la página entera en blanco**.
  setTimeout(() => ejecutarHooks(store.get('editorMode')), 150);
}

/** Recorre los hooks aplicando sus filtros, aislando cada uno. Único punto: el editor/creador y el
 *  sitio publicado tienen que comportarse igual ante un hook que falle. */
function ejecutarHooks(isEditor) {
  postRenderHooks.forEach(({ fn, editorOnly, previewOnly }) => {
      if (editorOnly && !isEditor) return;
      if (previewOnly && isEditor) return;
      try {
          fn();
      } catch (e) {
          console.error('Error running post-render hook:', e);
      }
  });
}

export function runPublicPostRenderHooks() {
  ejecutarHooks(false);
}

